import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  CalendarIcon, 
  Clock, 
  CheckCircle,
  Video,
  Mail,
  Phone,
  User,
  Loader2,
  ArrowRight
} from "lucide-react";
import { cn } from "@/lib/utils";

export const AppointmentsPage = () => {
  const { user, token } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    appointment_type: "consultation",
    preferred_time: "10:00",
    notes: ""
  });

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || "",
        email: user.email || ""
      }));
      fetchAppointments();
    }
  }, [user]);

  const fetchAppointments = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/appointments`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setAppointments(response.data.appointments || []);
    } catch (error) {
      console.error("Error fetching appointments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !date) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, {
        ...formData,
        preferred_date: format(date, 'yyyy-MM-dd')
      });
      toast.success("Appointment request submitted!");
      setSubmitted(true);
      if (token) fetchAppointments();
    } catch (error) {
      toast.error("Failed to submit appointment request");
    } finally {
      setSubmitting(false);
    }
  };

  const appointmentTypes = [
    { value: "consultation", label: "General Consultation", desc: "Discuss your investment goals" },
    { value: "portfolio_review", label: "Portfolio Review", desc: "Review your current investments" },
    { value: "tax_planning", label: "Tax Planning", desc: "Optimize your tax savings" },
    { value: "retirement", label: "Retirement Planning", desc: "Plan for your retirement" },
    { value: "wealth_management", label: "Wealth Management", desc: "Comprehensive wealth advisory" }
  ];

  const timeSlots = [
    "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
    "12:00", "14:00", "14:30", "15:00", "15:30", "16:00",
    "16:30", "17:00", "17:30", "18:00"
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "bg-yellow-500/10 text-yellow-500";
      case "confirmed": return "bg-green-500/10 text-green-500";
      case "completed": return "bg-blue-500/10 text-blue-500";
      case "cancelled": return "bg-red-500/10 text-red-500";
      default: return "bg-muted text-muted-foreground";
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background" data-testid="appointments-page">
        <Navbar />
        <main className="pt-28 pb-16 px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="font-serif text-3xl font-bold mb-4">Appointment Requested!</h1>
            <p className="text-muted-foreground mb-8">
              We've received your appointment request. Our team will confirm your slot and send you a meeting link within 24 hours.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Button onClick={() => setSubmitted(false)} variant="outline" className="border-white/10">
                Book Another
              </Button>
              <Button className="btn-primary" onClick={() => window.location.href = '/dashboard'}>
                Go to Dashboard
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="appointments-page">
      <Navbar />
      
      <main className="pt-28 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Book an <span className="gold-text">Appointment</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Schedule a one-on-one session with our investment advisors
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Booking Form */}
            <Card className="glass-card lg:col-span-2">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-primary" />
                  Schedule Your Session
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="name"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="pl-10 bg-secondary/50 border-white/10"
                          required
                          data-testid="apt-name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="john@example.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="pl-10 bg-secondary/50 border-white/10"
                          required
                          data-testid="apt-email-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+91 98765 43210"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="pl-10 bg-secondary/50 border-white/10"
                        required
                        data-testid="apt-phone-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Appointment Type *</Label>
                    <Select 
                      value={formData.appointment_type} 
                      onValueChange={(v) => setFormData({ ...formData, appointment_type: v })}
                    >
                      <SelectTrigger className="bg-secondary/50 border-white/10" data-testid="apt-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {appointmentTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            <div>
                              <span className="font-medium">{type.label}</span>
                              <span className="text-xs text-muted-foreground ml-2">- {type.desc}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Preferred Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal bg-secondary/50 border-white/10",
                              !date && "text-muted-foreground"
                            )}
                            data-testid="apt-date-btn"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {date ? format(date, "PPP") : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                          <Calendar
                            mode="single"
                            selected={date}
                            onSelect={setDate}
                            disabled={(date) => date < new Date() || date.getDay() === 0}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label>Preferred Time *</Label>
                      <Select 
                        value={formData.preferred_time} 
                        onValueChange={(v) => setFormData({ ...formData, preferred_time: v })}
                      >
                        <SelectTrigger className="bg-secondary/50 border-white/10" data-testid="apt-time-select">
                          <Clock className="w-4 h-4 mr-2" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border max-h-60">
                          {timeSlots.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time} IST
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Additional Notes</Label>
                    <Textarea
                      id="notes"
                      placeholder="Tell us what you'd like to discuss..."
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="bg-secondary/50 border-white/10 min-h-[100px]"
                      data-testid="apt-notes-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary py-6"
                    disabled={submitting}
                    data-testid="apt-submit-btn"
                  >
                    {submitting ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <CalendarIcon className="w-5 h-5 mr-2" />
                    )}
                    Book Appointment
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Info Cards */}
            <div className="space-y-6">
              <Card className="glass-card">
                <CardContent className="p-6">
                  <Video className="w-10 h-10 text-primary mb-4" />
                  <h3 className="font-semibold mb-2">Video Consultation</h3>
                  <p className="text-sm text-muted-foreground">
                    All sessions are conducted via video call. You'll receive a meeting link after confirmation.
                  </p>
                </CardContent>
              </Card>

              <Card className="glass-card">
                <CardContent className="p-6">
                  <Clock className="w-10 h-10 text-primary mb-4" />
                  <h3 className="font-semibold mb-2">30-45 Minutes</h3>
                  <p className="text-sm text-muted-foreground">
                    Each session lasts 30-45 minutes. Come prepared with your questions and financial documents.
                  </p>
                </CardContent>
              </Card>

              <Card className="glass-card">
                <CardContent className="p-6">
                  <CheckCircle className="w-10 h-10 text-green-500 mb-4" />
                  <h3 className="font-semibold mb-2">Free Consultation</h3>
                  <p className="text-sm text-muted-foreground">
                    First consultation is free. Get personalized advice without any commitment.
                  </p>
                </CardContent>
              </Card>

              {/* User's Appointments */}
              {user && appointments.length > 0 && (
                <Card className="glass-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Your Appointments</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {appointments.slice(0, 3).map((apt, index) => (
                      <div key={apt.appointment_id || index} className="p-3 bg-secondary/30 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium capitalize">
                            {apt.appointment_type?.replace('_', ' ')}
                          </span>
                          <Badge className={getStatusColor(apt.status)}>
                            {apt.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {apt.preferred_date} at {apt.preferred_time}
                        </p>
                        {apt.meeting_link && apt.status === 'confirmed' && (
                          <a 
                            href={apt.meeting_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline flex items-center gap-1 mt-2"
                          >
                            <Video className="w-3 h-3" />
                            Join Meeting
                          </a>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
